import { useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChatSurface } from '../components/chat/ChatSurface';
import {
  readParticipantTabs,
  writeParticipantTabs,
  type ParticipantTabsControl,
  type ParticipantTabsUrlState,
} from '../components/chat/chat-tabs';

/**
 * The `/chat` route. Session identity comes from the URL here; every other
 * host of `ChatSurface` binds it another way (app-shell ADR-013).
 */
export function ChatView() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const liveSearchParams = useRef(searchParams);
  liveSearchParams.current = searchParams;

  const openSession = useCallback(
    (id: string) => void navigate(`/chat/${id}`),
    [navigate],
  );
  const leaveMissingSession = useCallback(
    () => void navigate('/chat', { replace: true }),
    [navigate],
  );

  // (app-shell ADR-006)
  const writeTabs = useCallback(
    (mutate: (state: ParticipantTabsUrlState) => ParticipantTabsUrlState) => {
      const current = liveSearchParams.current;
      const next = new URLSearchParams(current);
      writeParticipantTabs(next, mutate(readParticipantTabs(current)));
      if (next.toString() === current.toString()) return;
      setSearchParams(next, { replace: true });
    },
    [setSearchParams],
  );

  const participantTabs = useMemo<ParticipantTabsControl | undefined>(() => {
    if (!sessionId) return undefined;
    const state = readParticipantTabs(searchParams);
    return {
      ...state,
      onOpen: (id) =>
        writeTabs((s) => ({
          openIds: s.openIds.includes(id) ? s.openIds : [...s.openIds, id],
          selectedId: id,
        })),
      onSelect: (id) => writeTabs((s) => ({ ...s, selectedId: id })),
      onClose: (id) =>
        writeTabs((s) => ({
          openIds: s.openIds.filter((open) => open !== id),
          selectedId: s.selectedId === id ? null : s.selectedId,
        })),
    };
  }, [sessionId, searchParams, writeTabs]);

  return (
    <ChatSurface
      sessionId={sessionId}
      onOpenSession={openSession}
      onSessionCreated={openSession}
      onSessionMissing={leaveMissingSession}
      participantTabs={participantTabs}
    />
  );
}
